/**
 * Document Dialogs Components
 * Upload (Single & Bulk) and Edit dialogs for documents
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Upload, Save, Loader2, FileUp, Files, X, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document types organized by category for better UX
const DOCUMENT_TYPES = [
  // Pay & Earnings
  { value: 'wage_slip', label: 'Wage Slip', category: 'Pay & Earnings' },
  // Tax Documents
  { value: 'p45', label: 'P45', category: 'Tax Documents' },
  { value: 'p60', label: 'P60', category: 'Tax Documents' },
  { value: 'p11d', label: 'P11D', category: 'Tax Documents' },
  // Travel
  { value: 'flight_stub', label: 'Flight Stub', category: 'Travel' },
  { value: 'boarding_pass', label: 'Boarding Pass', category: 'Travel' },
  { value: 'travel_receipt', label: 'Travel Receipt', category: 'Travel' },
  { value: 'vessel_positions', label: 'Vessel Midnight Positions', category: 'Travel' },
  // Receipts & Expenses
  { value: 'receipt', label: 'Receipt', category: 'Receipts' },
  { value: 'expense_claim', label: 'Expense Claim', category: 'Receipts' },
  { value: 'invoice', label: 'Invoice', category: 'Receipts' },
  // Other
  { value: 'seamans_discharge_book', label: "Seaman's Discharge Book", category: 'Other' },
  { value: 'other', label: 'Other', category: 'Other' },
];

// Pre-grouped for efficient rendering (no inline .filter() in JSX)
const DOC_CATEGORIES = ['Pay & Earnings', 'Tax Documents', 'Travel', 'Receipts', 'Other'];
const TYPES_BY_CATEGORY = Object.fromEntries(
  DOC_CATEGORIES.map(cat => [cat, DOCUMENT_TYPES.filter(t => t.category === cat)])
);

// Generate months for tax year selection
const getMonthOptions = (taxYear) => {
  if (!taxYear) return [];
  const [startYear, endYear] = taxYear.split('/').map(y => parseInt(y.length === 2 ? '20' + y : y));
  const months = [];
  for (let m = 4; m <= 12; m++) {
    months.push({ value: `${startYear}-${String(m).padStart(2, '0')}`, label: new Date(startYear, m - 1).toLocaleString('default', { month: 'long', year: 'numeric' }) });
  }
  for (let m = 1; m <= 3; m++) {
    months.push({ value: `${endYear}-${String(m).padStart(2, '0')}`, label: new Date(endYear, m - 1).toLocaleString('default', { month: 'long', year: 'numeric' }) });
  }
  return months;
};

export const UploadDialog = ({ taxYears, onSuccess, onClose }) => {
  const [mode, setMode] = useState('single'); // 'single' | 'bulk'
  const [file, setFile] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [taxYear, setTaxYear] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const bulkInputRef = useRef(null);

  useEffect(() => {
    if (taxYears.length > 0 && !taxYear) {
      setTaxYear(taxYears[0]);
    }
  }, [taxYears, taxYear]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleBulkFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => 
      f.type === 'application/pdf' || f.type.startsWith('image/')
    );
    if (validFiles.length < files.length) {
      toast.warning(`${files.length - validFiles.length} unsupported file(s) skipped`);
    }
    setBulkFiles(prev => [...prev, ...validFiles]);
  };

  const removeBulkFile = (index) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !taxYear || !documentType) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Content = reader.result.split(',')[1];
        await axios.post(`${API_URL}/api/documents`, {
          file_name: file.name,
          file_content: base64Content,
          tax_year: taxYear,
          document_type: documentType,
          month: month || null
        });
        toast.success('Document uploaded successfully');
        onSuccess();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload document');
      setLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (bulkFiles.length === 0 || !taxYear) {
      toast.error('Please select files and a tax year');
      return;
    }
    setLoading(true);
    setBulkProgress({ total: bulkFiles.length, done: 0, failed: 0 });

    try {
      const filesData = [];
      for (const f of bulkFiles) {
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(f);
        });
        filesData.push({
          file_name: f.name,
          file_content: base64,
        });
      }

      const response = await axios.post(`${API_URL}/api/documents/bulk-upload`, {
        tax_year: taxYear,
        document_type: documentType || 'wage_slip',
        files: filesData
      });

      const result = response.data;
      setBulkProgress({
        total: bulkFiles.length,
        done: result.successful || bulkFiles.length,
        failed: result.failed || 0,
        complete: true
      });

      if (result.failed > 0) {
        toast.warning(`${result.successful} uploaded, ${result.failed} failed`);
      } else {
        toast.success(`${result.successful || bulkFiles.length} documents uploaded successfully`);
      }
      
      setTimeout(() => onSuccess(), 1500);
    } catch (error) {
      toast.error('Bulk upload failed');
      setBulkProgress(null);
      setLoading(false);
    }
  };

  return (
    <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
      <DialogHeader>
        <DialogTitle className="text-white">Upload Documents</DialogTitle>
        <DialogDescription className="text-slate-400">
          Upload supporting documents for your tax claims
        </DialogDescription>
      </DialogHeader>

      {/* Mode Toggle */}
      <div className="flex border border-slate-600 rounded-lg overflow-hidden" data-testid="upload-mode-toggle">
        <button
          type="button"
          onClick={() => setMode('single')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 text-sm transition-colors ${
            mode === 'single' ? 'bg-amber-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
          }`}
          data-testid="upload-mode-single"
        >
          <FileUp className="h-4 w-4" />
          Single Upload
        </button>
        <button
          type="button"
          onClick={() => setMode('bulk')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 text-sm transition-colors ${
            mode === 'bulk' ? 'bg-amber-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
          }`}
          data-testid="upload-mode-bulk"
        >
          <Files className="h-4 w-4" />
          Bulk Upload
        </button>
      </div>

      {mode === 'single' ? (
        <form onSubmit={handleSingleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">File</Label>
            <Input
              type="file"
              onChange={handleFileChange}
              className="bg-slate-900 border-slate-600 text-white file:bg-slate-700 file:text-white file:border-0"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              data-testid="file-input"
            />
            {file && <p className="text-sm text-slate-400">Selected: {file.name}</p>}
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Tax Year</Label>
            <Select value={taxYear} onValueChange={setTaxYear}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="document-tax-year-select">
                <SelectValue placeholder="Select tax year" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {taxYears.map(year => (
                  <SelectItem key={year} value={year} className="text-white hover:bg-slate-700">{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Pay Month (optional)</Label>
            <Select value={month || "auto"} onValueChange={(val) => setMonth(val === "auto" ? "" : val)}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="month-select">
                <SelectValue placeholder="Auto-detect from filename" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 max-h-60">
                <SelectItem value="auto" className="text-slate-400 hover:bg-slate-700">Auto-detect from filename</SelectItem>
                {getMonthOptions(taxYear).map(m => (
                  <SelectItem key={m.value} value={m.value} className="text-white hover:bg-slate-700">{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              {documentType === 'wage_slip' 
                ? 'Select the month shown on the wage slip. It will be auto-assigned to the previous month (work month) since pay is 1 month in arrears.'
                : 'If not selected, will try to detect from filename'}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Document Type</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="document-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 max-h-72">
                {DOC_CATEGORIES.map(category => (
                  <SelectGroup key={category}>
                    <SelectLabel className="text-slate-500 text-xs uppercase tracking-wider px-2 py-1">{category}</SelectLabel>
                    {TYPES_BY_CATEGORY[category].map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-white hover:bg-slate-700">{type.label}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">Cancel</Button>
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={loading || !file} data-testid="upload-submit-btn">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading...</> : <><Upload className="mr-2 h-4 w-4" />Upload</>}
            </Button>
          </DialogFooter>
        </form>
      ) : (
        <form onSubmit={handleBulkSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Select Wage Slips (PDF or Images)</Label>
            <div 
              className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
              onClick={() => bulkInputRef.current?.click()}
              data-testid="bulk-dropzone"
            >
              <Files className="h-8 w-8 text-slate-500 mx-auto mb-2" />
              <p className="text-sm text-slate-400">Click to select multiple files</p>
              <p className="text-xs text-slate-500 mt-1">Ideal for uploading a year's worth of wage slips at once</p>
              <input
                ref={bulkInputRef}
                type="file"
                multiple
                onChange={handleBulkFileChange}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                data-testid="bulk-file-input"
              />
            </div>
            {bulkFiles.length > 0 && (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {bulkFiles.map((f, i) => (
                  <div key={`${f.name}-${f.size}`} className="flex items-center justify-between text-xs bg-slate-700/50 rounded px-2 py-1.5">
                    <span className="text-slate-300 truncate flex-1">{f.name}</span>
                    <button type="button" onClick={() => removeBulkFile(i)} className="text-slate-500 hover:text-red-400 ml-2">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <p className="text-xs text-slate-500">{bulkFiles.length} file(s) selected</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Tax Year</Label>
            <Select value={taxYear} onValueChange={setTaxYear}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="bulk-tax-year-select">
                <SelectValue placeholder="Select tax year" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {taxYears.map(year => (
                  <SelectItem key={year} value={year} className="text-white hover:bg-slate-700">{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Document Type</Label>
            <Select value={documentType || 'wage_slip'} onValueChange={setDocumentType}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="bulk-doc-type-select">
                <SelectValue placeholder="Wage Slip" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 max-h-72">
                {DOC_CATEGORIES.map(category => (
                  <SelectGroup key={category}>
                    <SelectLabel className="text-slate-500 text-xs uppercase tracking-wider px-2 py-1">{category}</SelectLabel>
                    {TYPES_BY_CATEGORY[category].map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-white hover:bg-slate-700">{type.label}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">AI will extract months from each document</p>
          </div>

          {/* Bulk Progress */}
          {bulkProgress && (
            <div className="bg-slate-700/50 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">
                  {bulkProgress.complete ? (
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Complete
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> Processing...
                    </span>
                  )}
                </span>
                <span className="text-slate-400 text-xs">{bulkProgress.done}/{bulkProgress.total}</span>
              </div>
              <div className="w-full bg-slate-600 rounded-full h-1.5">
                <div className="bg-amber-500 h-1.5 rounded-full transition-all" 
                  style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }} />
              </div>
              {bulkProgress.failed > 0 && (
                <p className="text-xs text-red-400 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {bulkProgress.failed} file(s) failed
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">Cancel</Button>
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={loading || bulkFiles.length === 0} data-testid="bulk-upload-submit-btn">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading {bulkFiles.length}...</> : <><Upload className="mr-2 h-4 w-4" />Upload {bulkFiles.length} Files</>}
            </Button>
          </DialogFooter>
        </form>
      )}
    </DialogContent>
  );
};

export const EditDocumentDialog = ({ document, taxYears, onSuccess, onClose }) => {
  const [fileName, setFileName] = useState('');
  const [taxYear, setTaxYear] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (document) {
      setFileName(document.file_name || '');
      setTaxYear(document.tax_year || '');
      setDocumentType(document.document_type || '');
      setMonth(document.month || '');
    }
  }, [document]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!document) return;

    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/documents/${document.id}`, {
        file_name: fileName,
        tax_year: taxYear,
        document_type: documentType,
        month: month || null
      });
      
      toast.success('Document updated successfully');
      onSuccess();
    } catch (error) {
      toast.error('Failed to update document');
    } finally {
      setLoading(false);
    }
  };

  if (!document) return null;

  return (
    <DialogContent className="bg-slate-800 border-slate-700">
      <DialogHeader>
        <DialogTitle className="text-white">Edit Document</DialogTitle>
        <DialogDescription className="text-slate-400">Update the document details</DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-slate-300">File Name</Label>
          <Input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            className="bg-slate-900 border-slate-600 text-white"
            data-testid="edit-file-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">Tax Year</Label>
          <Select value={taxYear} onValueChange={setTaxYear}>
            <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-600">
              {taxYears.map(year => (
                <SelectItem key={year} value={year} className="text-white hover:bg-slate-700">{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">Month</Label>
          <Select value={month || "auto"} onValueChange={(val) => setMonth(val === "auto" ? "" : val)}>
            <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-600 max-h-60">
              <SelectItem value="auto" className="text-slate-400 hover:bg-slate-700">Unassigned</SelectItem>
              {getMonthOptions(taxYear).map(m => (
                <SelectItem key={m.value} value={m.value} className="text-white hover:bg-slate-700">{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">Document Type</Label>
          <Select value={documentType} onValueChange={setDocumentType}>
            <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-600 max-h-72">
              {DOC_CATEGORIES.map(category => (
                <SelectGroup key={category}>
                  <SelectLabel className="text-slate-500 text-xs uppercase tracking-wider px-2 py-1">{category}</SelectLabel>
                  {TYPES_BY_CATEGORY[category].map(type => (
                    <SelectItem key={type.value} value={type.value} className="text-white hover:bg-slate-700">{type.label}</SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">Cancel</Button>
          <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={loading || !fileName} data-testid="edit-submit-btn">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : <><Save className="mr-2 h-4 w-4" />Save Changes</>}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};
